import React, { useState } from 'react';
import { CsvImport } from '../../data/types';
import Button from '../ui/Button';

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (file: File) => Promise<CsvImport>;
  entityName: string;
}

const CsvImportModal: React.FC<CsvImportModalProps> = ({
  isOpen,
  onClose,
  onImport,
  entityName
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<CsvImport | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setError('Please select a valid CSV file');
        setFile(null);
      } else {
        setFile(selectedFile);
        setError(null);
      }
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file to import');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const result = await onImport(file);
      setImportResult(result);
    } catch (err) {
      setError(`Import failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setImportResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Import {entityName} from CSV</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!importResult ? (
          <>
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Upload a CSV file to import {entityName.toLowerCase()}. The file should have the following columns:
              </p>
              <div className="bg-gray-50 p-3 rounded-md text-xs font-mono">
                {entityName === 'Classes' && 
                  'name,grade,schoolId,schoolYear,teacherId,teachers (optional - comma separated IDs)'}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select CSV File
              </label>
              <input
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
              {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            </div>

            <div className="flex justify-end space-x-3">
              <Button 
                variant="secondary" 
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleImport}
                disabled={!file || isLoading}
              >
                {isLoading ? 'Importing...' : 'Import'}
              </Button>
            </div>
          </>
        ) : (
          <div className="text-center py-4">
            <div className={`mb-4 ${
              importResult.status === 'success' ? 'text-green-500' : 
              importResult.status === 'partial' ? 'text-yellow-500' : 
              'text-red-500'
            }`}>
              {importResult.status === 'success' ? (
                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : importResult.status === 'partial' ? (
                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              ) : (
                <svg className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </div>
            
            <h3 className="text-lg font-medium mb-2">
              {importResult.status === 'success' ? 'Import Successful' : 
               importResult.status === 'partial' ? 'Partially Imported' : 
               'Import Failed'}
            </h3>
            
            <div className="mb-4 text-sm">
              <p>Records processed: {importResult.recordsProcessed}</p>
              <p>Records succeeded: {importResult.recordsSucceeded}</p>
              <p>Records failed: {importResult.recordsFailed}</p>
            </div>
            
            {importResult.errors.length > 0 && (
              <div className="mb-4 text-left">
                <h4 className="font-medium mb-2">Errors:</h4>
                <div className="max-h-40 overflow-y-auto bg-gray-50 p-3 rounded-md text-xs">
                  {importResult.errors.map((error, index) => (
                    <p key={index} className="mb-1">
                      Row {error.row}: {error.message}
                    </p>
                  ))}
                </div>
              </div>
            )}
            
            <Button 
              variant="primary" 
              onClick={handleClose}
              className="mt-2"
            >
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CsvImportModal;
